import { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

function Tunnel({ count = 3000 }) {
  const mesh = useRef()
  const dummy = useMemo(() => new THREE.Object3D(), [])

  const particles = useMemo(() => {
    const arr = []
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2
      const radius = 1.5 + Math.random() * 6
      arr.push({
        angle,
        radius,
        z: -Math.random() * 80,
        speed: 0.3 + Math.random() * 0.6,
        size: 0.02 + Math.random() * 0.06,
        drift: (Math.random() - 0.5) * 0.3,
      })
    }
    return arr
  }, [count])

  const colors = useMemo(() => {
    const c = new Float32Array(count * 3)
    const palette = [
      [1.0, 0.4, 0.2],
      [1.0, 0.6, 0.1],
      [1.0, 0.2, 0.4],
      [0.9, 0.3, 0.9],
      [0.4, 0.3, 1.0],
      [1.0, 0.85, 0.3],
    ]
    for (let i = 0; i < count; i++) {
      const p = palette[Math.floor(Math.random() * palette.length)]
      c[i * 3] = p[0]
      c[i * 3 + 1] = p[1]
      c[i * 3 + 2] = p[2]
    }
    return c
  }, [count])

  useFrame((state) => {
    const t = state.clock.elapsedTime
    particles.forEach((p, i) => {
      p.z += p.speed
      if (p.z > 5) p.z = -80 + Math.random() * 5

      const x = Math.cos(p.angle + t * p.drift) * p.radius
      const y = Math.sin(p.angle + t * p.drift) * p.radius

      dummy.position.set(x, y, p.z)
      const s = p.size * (1 + Math.sin(t * 2 + i) * 0.3)
      dummy.scale.set(s, s, s)
      dummy.updateMatrix()
      mesh.current.setMatrixAt(i, dummy.matrix)
    })
    mesh.current.instanceMatrix.needsUpdate = true
  })

  return (
    <instancedMesh ref={mesh} args={[null, null, count]}>
      <sphereGeometry args={[1, 6, 6]}>
        <instancedBufferAttribute attach="attributes-color" args={[colors, 3]} />
      </sphereGeometry>
      <meshBasicMaterial
        vertexColors
        transparent
        opacity={0.85}
        toneMapped={false}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </instancedMesh>
  )
}

function Rings() {
  const group = useRef()

  useFrame((state) => {
    const t = state.clock.elapsedTime
    group.current.children.forEach((ring, i) => {
      ring.rotation.z = t * 0.15 * (i % 2 === 0 ? 1 : -1) + i
      ring.position.z = -i * 8 + ((t * 0.8 + i * 3) % 80) - 40
      const proximity = 1 - Math.abs(ring.position.z) / 40
      ring.scale.setScalar(Math.max(0.1, proximity * 1.5))
      ring.material.opacity = Math.max(0, proximity * 0.25)
    })
  })

  return (
    <group ref={group}>
      {Array.from({ length: 12 }, (_, i) => (
        <mesh key={i} position={[0, 0, -i * 8]}>
          <torusGeometry args={[4 + i * 0.3, 0.015, 8, 80]} />
          <meshBasicMaterial
            color={i % 3 === 0 ? '#ff6040' : i % 3 === 1 ? '#ff40a0' : '#6040ff'}
            transparent
            opacity={0.2}
            toneMapped={false}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      ))}
    </group>
  )
}

export default function Scene() {
  const group = useRef()

  useFrame((state) => {
    group.current.rotation.z = Math.sin(state.clock.elapsedTime * 0.08) * 0.1
  })

  return (
    <group ref={group}>
      <Tunnel />
      <Rings />
    </group>
  )
}
